import React, { useMemo } from 'react';
import {
  injectIntl,
  WrappedComponentProps,
  FormattedMessage
} from '@kyruus/intl';
import { Alert } from '@biome-design-system/ui/Alert';
import { ProviderInfoListBox } from '@kyruus/provider-components';
import { ProviderMatchProviderV9, PMCConfig } from '@kyruus/types';

import ExpandableCardContainer from '../ExpandableCardContainer/expandable-card-container';
import { DisclaimerContainer, InsurancesAcceptedContainer } from './styles';
import messages from '../../lib/messages';
import { TrackingFunction } from '../../lib/tracking-utils';
import { sortByName, pruneIgnoredValues } from '../../lib/utils';
import { withErrorBoundary } from '../ErrorFallback/with-error-boundary';

interface ProviderInsurancesProps {
  provider: ProviderMatchProviderV9;
  config: PMCConfig;
  /** analytics function to track interactions */
  track: TrackingFunction;
}

const ProviderInsuranceAccepted = ({
  intl,
  provider,
  config,
  track
}: ProviderInsurancesProps & WrappedComponentProps): React.ReactElement => {
  const { insurance_accepted } = pruneIgnoredValues(
    provider,
    'insurance_accepted',
    config
  );

  const sortedInsuranceNameObjects = useMemo(
    () => sortByName(insurance_accepted.map((name: string) => ({ name }))),
    [insurance_accepted]
  );

  return (
    <ExpandableCardContainer id="profile-insurance-section" track={track}>
      <InsurancesAcceptedContainer>
        <ProviderInfoListBox
          id={`${provider.id}-insurance-accepted`}
          cssClasses="provider-insurances"
          title={intl.formatMessage(messages.insuranceComponentTitle)}
          itemType={intl.formatMessage(
            messages.insuranceComponentSearchAriaLabelItemType
          )}
          searchInputTitle={
            <FormattedMessage {...messages.insuranceComponentSearchTitle} />
          }
          items={sortedInsuranceNameObjects}
          showClearSearchIcon={true}
          numColumns={2}
          log={track as (event: string) => void}
          message={
            <DisclaimerContainer>
              <Alert severity="info" variant="outlined">
                <FormattedMessage {...messages.insuranceDisclaimerMessage} />
              </Alert>
            </DisclaimerContainer>
          }
          isV9Profile={true}
          showSearch={true}
        />
      </InsurancesAcceptedContainer>
    </ExpandableCardContainer>
  );
};

export default withErrorBoundary(injectIntl(ProviderInsuranceAccepted));
